'use client';
import { useState } from 'react';
import type { Route } from './+types/pricing';
import { Link } from 'react-router';
import { RelatedPages } from '~/components/related-pages';
import { PRICING, SITE, calcPrice, formatPrice } from '~/data/site';
import { faqsFor } from '~/data/page-schemas';
import { buildMeta, schemaFor, faqPageSchema } from '~/lib/seo';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Pricing',
    description:
      'Per-square-foot pricing published on this page. No quotes required. See exactly what your home costs before you reach for the phone.',
    path: '/pricing',
    jsonLd: schemaFor('/pricing'),
  }),
  {
    // Same list the FAQ section renders — see faqsFor('/pricing').
    'script:ld+json': faqPageSchema(faqsFor('/pricing')),
  },
];

type ServiceKey = 'weekly' | 'biweekly' | 'every4' | 'maintenance' | 'fulldeep' | 'moveout';

const SERVICES: { key: ServiceKey; label: string; rate: number; recurring: boolean }[] = [
  { key: 'weekly',      label: 'Weekly',           rate: PRICING.weeklyRate,      recurring: true },
  { key: 'biweekly',    label: 'Biweekly',         rate: PRICING.biweeklyRate,    recurring: true },
  { key: 'every4',      label: 'Every 4 weeks',    rate: PRICING.monthly4Rate,    recurring: true },
  { key: 'maintenance', label: 'Maintenance deep', rate: PRICING.maintenanceRate, recurring: false },
  { key: 'fulldeep',    label: 'Full deep clean',  rate: PRICING.fullDeepRate,    recurring: false },
  { key: 'moveout',     label: 'Move-in / out',    rate: PRICING.moveOutRate,     recurring: false },
];

function PriceCalculator() {
  const [sqft, setSqft] = useState(2400);
  const [selected, setSelected] = useState<ServiceKey>('biweekly');
  const [deepUpgrade, setDeepUpgrade] = useState(false);

  const service = SERVICES.find((s) => s.key === selected)!;
  const price = sqft > 0 ? calcPrice(sqft, service.key) : 0;
  const upgradePrice = sqft > 0 ? Math.round(sqft * PRICING.deepUpgradeRate) : 0;
  const isRecurring = service.recurring;
  const atFloor = sqft > 0 && sqft * service.rate < PRICING.minimum;

  return (
    <div className="calc-widget reveal">
      <div className="calc-row">
        <span className="lede lede-17">My home is</span>
        <input
          type="number"
          className="calc-input"
          value={sqft || ''}
          min={100}
          max={10000}
          onChange={(e) => setSqft(Math.max(0, parseInt(e.target.value) || 0))}
          aria-label="Square footage"
        />
        <span className="lede lede-17">sq ft</span>
      </div>

      <div className="calc-services">
        {SERVICES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`calc-btn${selected === s.key ? ' active' : ''}`}
            onClick={() => { setSelected(s.key); setDeepUpgrade(false); }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {isRecurring && sqft > 0 && (
        <label className={`calc-upgrade visible`}>
          <input
            type="checkbox"
            checked={deepUpgrade}
            onChange={(e) => setDeepUpgrade(e.target.checked)}
          />
          <span>Add first-visit deep clean (+{formatPrice(upgradePrice)})</span>
        </label>
      )}

      {sqft > 0 ? (
        <div className="calc-result">
          <div className="calc-price">{formatPrice(price)}</div>
          <p className="calc-formula">
            {sqft.toLocaleString()} sqft × ${service.rate.toFixed(2)}
            {atFloor ? ` — ${PRICING.minimum} minimum applies` : ''}
            {' '}· {isRecurring ? 'per visit' : 'flat rate'}
          </p>
          {deepUpgrade && isRecurring && (
            <p className="calc-formula">
              First visit: {formatPrice(price + upgradePrice)} (includes deep clean)
            </p>
          )}
        </div>
      ) : (
        <div className="calc-result">
          <div className="calc-price text-mute">—</div>
          <p className="calc-formula">Enter your home&rsquo;s square footage above</p>
        </div>
      )}

      <div className="section-cta-left">
        <Link to="/quote" className="btn btn-brass">Get this quote →</Link>
      </div>
    </div>
  );
}

export default function PricingPage() {
  return (
    <main id="main-content">
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">The math, in the open</p>
          <h1 className="display-lg page-h1-full">
            Where <em className="italic-display">every dollar</em> of your visit goes.
          </h1>
          <p className="lede chapter-lede">
            We published this page because the cleaning industry hides its math. We don&rsquo;t.
            You can trace your biweekly visit from the moment it hits our account to the moment
            it lands in your crew&rsquo;s paycheck.
          </p>
        </div>
      </section>

      {/* ── CALCULATOR ───────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">Find your price.</h2>
          <p className="lede chapter-lede">
            Type your square footage and pick a service. That&rsquo;s it.
          </p>
          <PriceCalculator />
        </div>
      </section>

      {/* ── WHY PER-SQUARE-FOOT ──────────────────────────────
          Rewritten 2026-09-10. The previous version ("Because hours are
          dishonest… we're paid the same whether we hustle or stroll") argued a
          virtue we cannot demonstrate, and was not even true of the business —
          the whole model is built on budgeted labour hours. The real, checkable
          differentiator is the one on this page: the number is published, you
          get it without talking to anyone, and it does not move. */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why per-square-foot</p>
          <h2 className="display-md">
            You get the number{' '}
            <em className="italic-display">before you call us</em>.
          </h2>
          <p className="lede chapter-lede">
            Square footage is the one thing about your home we can both measure the
            same way. So we published the rate, put the calculator on this page, and
            let you price the job yourself &mdash; no walkthrough, no &ldquo;starting
            at,&rdquo; no waiting on a callback to find out if you can afford us.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            The price you see is the price we book. It doesn&rsquo;t change once
            we&rsquo;ve seen the house, and it doesn&rsquo;t creep three months in.
            If your home changes &mdash; you finish the garage, you add a room &mdash;
            we re-measure and tell you before the visit, not after.
          </p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────
          Rendered from page-schemas.ts, the same list that becomes this page's
          FAQPage JSON-LD, so visible text and structured data cannot drift.
          /pricing previously emitted no FAQ at all — a gap on the site's
          highest-intent page. Add questions in page-schemas.ts. */}
      <section className="faq-section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Common questions</p>
          <h2 className="display-md faq-heading">Pricing, answered.</h2>
          <div className="faq-list">
            {faqsFor('/pricing').map((faq) => (
              <details className="faq-item" key={faq.question}>
                <summary className="faq-question">{faq.question}</summary>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <RelatedPages path="/pricing" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready?</p>
          <h2>Want the number for <em>your home?</em></h2>
          <p className="cta-lede">See it in under a minute. No call required.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">See your price →</Link>
        </div>
      </section>
    </main>
  );
}
